<template>
  <div class="demo-panel">
    <section class="panel-header-section">
      <h2 class="section-title">Signal Diagnostics</h2>
      <p class="section-subtitle">Test endpoints, verify connectivity, and inspect real-time counters.</p>
    </section>

    <section class="cards-grid">
      <div class="diag-card">
        <div class="card-header">
          <span class="card-label">Counter A</span>
          <span class="card-pulse"></span>
        </div>
        <div class="card-value">{{ counter1 }}</div>
        <button class="card-action" @click="counter1++">Increment</button>
      </div>

      <div class="diag-card">
        <div class="card-header">
          <span class="card-label">Counter B</span>
          <span class="card-pulse"></span>
        </div>
        <div class="card-value">{{ counter2 }}</div>
        <button class="card-action" @click="counter2++">Increment</button>
      </div>

      <div class="diag-card">
        <div class="card-header">
          <span class="card-label">Counter C</span>
          <span class="card-pulse"></span>
        </div>
        <div class="card-value">{{ counter3 }}</div>
        <button class="card-action" @click="counter3++">Increment</button>
      </div>
    </section>

    <section class="console-panel">
      <div class="console-header">
        <h3>Exchange Rate Probe</h3>
        <button class="probe-btn" :class="{ active: isActive }" @click="getCourse">
          <span v-if="!isActive">Fetch NBRB</span>
          <span v-else>Refreshing...</span>
        </button>
      </div>
      <div class="console-body">
        <div v-if="course" class="probe-result success">
          <span class="probe-label">Response</span>
          <span class="probe-data">{{ course }}</span>
        </div>
        <div v-else-if="error" class="probe-result error">
          <span class="probe-label">Error</span>
          <span class="probe-data">{{ error.statusCode }} — {{ error.message }}</span>
        </div>
        <div v-else class="probe-placeholder">
          Press the button above to probe the National Bank API.
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SignalDiagnostics',
  props: {
    msg: String
  },
  data() {
    return {
      isActive: false,
      counter1: 0,
      counter2: 0,
      counter3: 0,
      course: '',
      error: ''
    }
  },
  methods: {
    getCourse() {
      this.isActive = true
      this.axios.get('https://www.nbrb.by/api/exrates/rates?periodicity=0')
          .then(res => {
            this.course = res.data[21].Cur_Scale + ' ' + res.data[21].Cur_Abbreviation
            this.error = ''
          })
          .catch(err => {
            if (err.response && err.response.status >= 500) {
              this.error = { statusCode: err.response.status, message: 'Server error' }
            } else if (err.response) {
              this.error = { statusCode: err.response.status, message: err.response.data.message || 'Request failed' }
            } else {
              this.error = { statusCode: 0, message: err.message }
            }
            this.course = ''
          })
          .finally(() => {
            setTimeout(() => { this.isActive = false }, 400)
          })
    }
  },
  mounted() {
    this.counter1 = 1
    this.counter2 = 1
    this.counter3 = 1
  }
}
</script>

<style scoped>
.demo-panel {
  max-width: 1200px;
}

.panel-header-section {
  margin-bottom: 28px;
}

.section-title {
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0 0 8px;
}

.section-subtitle {
  color: var(--text-secondary);
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.diag-card {
  padding: 24px;
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: transform 0.2s ease;
}

.diag-card:hover {
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.card-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 10px var(--accent);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.card-value {
  font-family: var(--font-mono);
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
}

.card-action {
  padding: 12px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.card-action:hover {
  background: var(--surface-hover);
  border-color: var(--accent);
  color: var(--accent);
}

.console-panel {
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
  overflow: hidden;
}

.console-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border);
}

.console-header h3 {
  font-family: var(--font-display);
  font-size: 1.05rem;
  margin: 0;
}

.probe-btn {
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.probe-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.probe-btn.active {
  background: var(--accent-dim);
  border-color: var(--accent);
  color: var(--accent);
}

.console-body {
  padding: 24px;
  font-family: var(--font-mono);
  font-size: 0.9rem;
}

.probe-result {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 8px;
}

.probe-result.success {
  background: rgba(0, 230, 118, 0.08);
  border: 1px solid rgba(0, 230, 118, 0.2);
}

.probe-result.error {
  background: var(--danger-bg);
  border: 1px solid rgba(255, 82, 82, 0.2);
}

.probe-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.probe-data {
  color: var(--text-primary);
}

.probe-result.success .probe-data {
  color: var(--success);
}

.probe-result.error .probe-data {
  color: var(--danger);
}

.probe-placeholder {
  color: var(--text-muted);
}

@media (max-width: 760px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .console-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
